/**
 * TanStack Query Cache Configuration
 *
 * Defines cache durations based on how frequently data changes
 */

export const CACHE_CONFIG = {
  // Very dynamic data (changes every few minutes)
  VERY_DYNAMIC: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  },

  // Dynamic data (changes every 5-10 minutes)
  DYNAMIC: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
  },

  // Semi-static data (changes every 10-30 minutes)
  SEMI_STATIC: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  },

  // Static data (rarely changes)
  STATIC: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  },

  // Very static data (changes very rarely)
  VERY_STATIC: {
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
  },
};

/**
 * Resource-specific cache configurations
 */
export const RESOURCE_CACHE = {
  // Inventory & Sales (very dynamic)
  inventory: CACHE_CONFIG.VERY_DYNAMIC,
  sales: CACHE_CONFIG.VERY_DYNAMIC,

  // Products & Services (semi-static)
  products: CACHE_CONFIG.SEMI_STATIC,
  services: CACHE_CONFIG.SEMI_STATIC,

  // Purchases (dynamic)
  purchases: CACHE_CONFIG.DYNAMIC,

  // Transfers & Requests (dynamic)
  transfers: CACHE_CONFIG.DYNAMIC,
  transferRequests: CACHE_CONFIG.VERY_DYNAMIC, // More dynamic
  removals: CACHE_CONFIG.SEMI_STATIC,

  // Mappings (static)
  productMappings: CACHE_CONFIG.STATIC,
  mappingSuggestions: CACHE_CONFIG.STATIC,

  // Orders & Tickets (dynamic)
  orders: CACHE_CONFIG.DYNAMIC,
  tickets: CACHE_CONFIG.DYNAMIC,

  // Promo Codes (semi-static)
  promoCodes: CACHE_CONFIG.SEMI_STATIC,
  promoUsage: CACHE_CONFIG.DYNAMIC, // Usage data changes more frequently

  // Settings & Config (very static)
  storeSettings: CACHE_CONFIG.VERY_STATIC,
  groups: CACHE_CONFIG.STATIC,
  groupSettings: CACHE_CONFIG.VERY_STATIC,
  users: CACHE_CONFIG.STATIC,
  permissions: CACHE_CONFIG.VERY_STATIC,

  // EBM Transactions (dynamic - status changes frequently)
  ebmTransactions: CACHE_CONFIG.DYNAMIC,
  ebmTransactionDetails: CACHE_CONFIG.DYNAMIC,
  ebmTransactionStats: CACHE_CONFIG.DYNAMIC,

  // Request Logs (semi-static - logs don't change once created)
  requestLogs: CACHE_CONFIG.SEMI_STATIC,
  requestLogDetails: CACHE_CONFIG.SEMI_STATIC,
};

/**
 * Query keys factory for consistent naming
 */
export const queryKeys = {
  // Transfers
  transfers: (groupId, filters) => ["transfers", groupId, filters],
  transfer: (groupId, transferId) => ["transfer", groupId, transferId],

  // Transfer Requests
  transferRequests: (groupId, filters) => [
    "transferRequests",
    groupId,
    filters,
  ],
  transferRequest: (groupId, requestId) => [
    "transferRequest",
    groupId,
    requestId,
  ],

  // Removals
  removals: (storeId, page) => ["removals", storeId, page],

  // Product Mappings
  mappings: (storeId, filters) => ["productMappings", storeId, filters],
  mapping: (storeId, mappingId) => ["productMapping", storeId, mappingId],
  mappingSuggestions: (storeId, otherStoreId) => [
    "mappingSuggestions",
    storeId,
    otherStoreId,
  ],

  // Products & Inventory
  products: (storeId, filters) => ["products", storeId, filters],
  inventory: (storeId, filters) => ["inventory", storeId, filters],

  // Groups
  groups: (storeId) => ["groups", storeId],
  group: (groupId) => ["group", groupId],
  groupStores: (groupId) => ["groupStores", groupId],

  // Promo Codes
  promoCodes: (storeId) => ["promoCodes", storeId],
  promoCode: (storeId, promoCodeId) => ["promoCode", storeId, promoCodeId],
  storePromoUsage: (storeId, filters) => ["storePromoUsage", storeId, filters],
  groupPromoUsage: (groupId, filters) => ["groupPromoUsage", groupId, filters],

  // EBM Transactions
  ebmTransactions: (storeId, filters) => ["ebmTransactions", storeId, filters],
  ebmTransactionDetails: (uuid) => ["ebmTransactionDetails", uuid],
  ebmTransactionStats: (storeId, dateRange) => [
    "ebmTransactionStats",
    storeId,
    dateRange,
  ],

  // Request Logs
  requestLogs: (filters) => ["requestLogs", filters],
  requestLogDetails: (id) => ["requestLogDetails", id],
};
